import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import BYDBanner from './images/kv-pc.jpg';
import BYDBannerMobile from './images/01-kv-banner-mob.webp';
import SeagullExterior1 from './images/07-01-pc-01.webp';
import SeagullExterior2 from './images/2023-byd-seagull.jpg';
import SeagullInterior5 from './images/10-pc.jpg';
import SeagullInterior11 from './images/10-01-pc-02.webp';
import SeagullCharging15 from './images/10-01-pc-04.jpg';
import SeagullPerformance16 from './images/08-01-pc-03.webp';
import Seagull25 from './images/07-02-pc-01.webp';
import SeagullBlack from './images/04-black.webp';
import SeagullBlue from './images/02-blue.webp';
import SeagullGreen from './images/byd-green.png';
import SeagullWhite from './images/03-white.webp';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

function JelajahiSeagull() {
  const sections = useRef([]);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [visibleSections, setVisibleSections] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedColor, setSelectedColor] = useState('black');

  const colorOptions = [
    { label: 'Cosmos Black', color: 'black', image: SeagullBlack },
    { label: 'Sprout Green', color: 'limegreen', image: SeagullBlue },
    { label: 'Apricity White', color: 'white', image: SeagullWhite },
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const images = [
    {
      src: isMobile ? BYDBannerMobile : BYDBanner,
      title: 'BYD Atto 1',
      specs: [
        { value: '55 kW', label: 'Tenaga Maksimal' },
        { value: '135 Nm', label: 'Torsi Maksimal' },
        { value: '4.9 detik', label: '0–50 km/jam' },
        { value: '300–380 km', label: 'Jarak Tempuh NEDC' }, // disesuaikan dari PDF
        { value: '30.08 – 38.88 kWh', label: 'Kapasitas Baterai' },
        { value: '130 km/jam', label: 'Kecepatan Maksimum' },
      ],
    },
    {
      src: SeagullExterior1,
      title: 'Desain Ringkas & Futuristik',
      description: `Dengan dimensi 3925 x 1720 x 1550 mm dan garis bodi tegas, BYD Atto 1 memadukan kepraktisan urban dengan gaya futuristik. Velg alloy 16 inci dan DRL LED menambah kesan sporty. Cocok untuk mobilitas harian di kota.`,
    },
    {
      src: SeagullExterior2,
      title: 'Tampil Modern & Agresif',
      description: `Tampilan luar BYD Atto 1 dibuat ramping namun kuat. Dengan jarak sumbu roda 2500 mm dan ground clearance 155 mm, mobil ini lincah, stabil, dan tetap elegan di tengah keramaian kota.`,
    },
    {
      src: SeagullInterior5,
      title: 'Interior Ergonomis & Canggih',
      description:
        'Interior dilengkapi layar sentuh 10,1", panel instrumen digital 7", wireless charging*, dan kontrol suara pintar. Semua dikemas dalam kabin ergonomis dua warna yang nyaman dan modern. *tersedia di varian Premium.',
    },
    {
      src: SeagullInterior11,
      title: 'Kenyamanan Pengemudi Maksimal',
      description:
        'Setir multifungsi, sistem head unit yang dapat diputar, serta pengaturan kursi elektrik (Premium) memberikan kenyamanan dan kontrol penuh bagi pengemudi.',
    },
    {
      src: SeagullCharging15,
      title: 'Fleksibel & Praktis',
      description:
        'Kursi belakang bisa dilipat penuh untuk menambah ruang bagasi. Cocok membawa barang besar tanpa mengorbankan kenyamanan penumpang.',
    },
    {
      src: SeagullPerformance16,
      title: 'Fitur Keamanan Lengkap',
      description:
        'BYD Atto 1 dilengkapi ABS+EBD, airbag depan & tirai, ESC, ISOFIX, TPMS, dan sistem pengereman regeneratif. Semua fitur hadir di kedua varian, menjamin keselamatan aktif & pasif.',
    },
    {
      src: Seagull25,
      title: 'Mudah Dikendalikan di Jalanan Kota',
      description:
        'Radius putar minimum 4,95 meter dan suspensi MacPherson depan membuat manuver mudah di ruang sempit. Atto 1 ideal untuk pemakaian harian di area urban.',
    },
    {
      type: 'colorPicker',
      title: '3 Pilihan Warna Premium',
    },
  ];

  const handleScroll = useCallback(
    (e) => {
      e.preventDefault();
      const delta = e.deltaY;

      if (delta > 0 && scrollIndex < images.length - 1) {
        setScrollIndex((prev) => prev + 1);
      } else if (delta < 0 && scrollIndex > 0) {
        setScrollIndex((prev) => prev - 1);
      }
    },
    [scrollIndex, images.length]
  );

  useEffect(() => {
    const currentSections = sections.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const updatedVisibleSections = { ...visibleSections };
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            updatedVisibleSections[entry.target.dataset.index] = true;
          }
        });
        setVisibleSections(updatedVisibleSections);
      },
      { threshold: 0.5 }
    );

    currentSections.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      currentSections.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, [visibleSections]);

  useEffect(() => {
    window.addEventListener('wheel', handleScroll, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    sections.current[scrollIndex]?.scrollIntoView({ behavior: 'smooth' });
  }, [scrollIndex]);

  return (
    <>
      <Helmet>
        <title>BYD Atto 1 - Mobil Listrik Kompak Futuristik</title>
        <meta
          name='description'
          content='Temukan BYD Atto 1 dengan baterai Blade, layar sentuh 10.1", CarPlay & Android Auto, serta desain kompak nan stylish. Tersedia varian Dynamic & Premium. Lihat spesifikasi lengkap dan warna pilihan di Dealer Resmi BSD Serpong.'
        />
        <meta
          name='keywords'
          content='BYD Atto 1, mobil listrik BYD, harga BYD Atto 1, spesifikasi BYD Atto 1, dealer BYD BSD Serpong, mobil listrik compact, BYD Atto1'
        />
        <meta name='author' content='BYD BSD Serpong Junius' />
        <meta name='geo.region' content='ID-BT' />
        <meta
          name='geo.placename'
          content='BSD Serpong, Tangerang Selatan, Indonesia'
        />
        <meta name='geo.position' content='-6.3046;106.6496' />
        <meta name='ICBM' content='-6.3046, 106.6496' />
      </Helmet>

      <Typography
        variant='h1'
        component='h1'
        sx={{
          fontSize: { xs: '2rem', sm: '3rem' },
          textAlign: 'center',
          marginBottom: 4,
        }}
      >
        Jelajahi BYD BYD Atto 1
      </Typography>

      <Box>
        {images.map((img, index) => (
          <Box
            key={index}
            ref={(el) => (sections.current[index] = el)}
            data-index={index}
            sx={{
              position: 'relative',
              height: '100vh',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {img.type === 'colorPicker' ? (
              <>
                <Box
                  component='img'
                  src={
                    selectedColor === 'black'
                      ? SeagullBlack
                      : selectedColor === 'white'
                      ? SeagullWhite
                      : SeagullGreen
                  }
                  alt='Warna BYD BYD Atto 1'
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    zIndex: -1,
                  }}
                />

                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '15%',
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    zIndex: 1,
                  }}
                >
                  {colorOptions.map((option) => (
                    <Button
                      key={option.color}
                      variant={
                        selectedColor === option.color
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() => setSelectedColor(option.color)}
                      sx={{
                        color: selectedColor === option.color ? '#fff' : '#000',
                        border: '2px solid #ccc',
                        borderRadius: '50%',
                        minWidth: 48,
                        height: 48,
                        padding: 0,
                        backgroundColor: option.color,
                        boxShadow:
                          option.color === 'white'
                            ? '0 0 0 2px rgba(0,0,0,0.3)' // outline for white
                            : '0 0 4px rgba(0,0,0,0.2)',
                        '&:hover': {
                          opacity: 0.9,
                        },
                      }}
                    ></Button>
                  ))}
                </Box>
              </>
            ) : (
              <Box
                component='img'
                src={img.src}
                alt={`${
                  index === 0
                    ? 'BYD Sealion 7 - Banner menampilkan desain eksterior SUV listrik'
                    : img.title
                    ? `${img.title} - ${
                        img.description || 'Fitur BYD Sealion 7'
                      }`
                    : `BYD Sealion 7 - Gambar ${index + 1}`
                }`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  zIndex: -1,
                }}
              />
            )}

            {img.title && (
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: '10%', sm: '15%' },
                  color: '#fff',
                  textAlign: 'center',
                  padding: 2,
                  transform: visibleSections[index]
                    ? 'translateY(0)'
                    : 'translateY(100%)',
                  opacity: visibleSections[index] ? 1 : 0,
                  transition: 'all 0.8s ease-in-out',
                }}
              >
                <Typography
                  variant='h4'
                  fontWeight='bold'
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                  }}
                >
                  {img.title}
                </Typography>
              </Box>
            )}

            {img.specs && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: { xs: '10%', sm: '5%' },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'center',
                  gap: { xs: 2, sm: 4 },
                  color: '#fff',
                }}
              >
                {img.specs.map((spec, i) => (
                  <React.Fragment key={i}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant='h6'
                        fontWeight='bold'
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                        }}
                      >
                        {spec.value}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                        }}
                      >
                        {spec.label}
                      </Typography>
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
            )}

            {img.description && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: { xs: '15%', sm: '10%' },
                  color: '#fff',
                  textAlign: 'center',
                  padding: 2,
                  maxWidth: { xs: '90%', sm: '80%' },
                  background: 'rgba(0, 0, 0, 0.5)',
                  borderRadius: '8px',
                  transform: visibleSections[index]
                    ? 'translateY(0)'
                    : 'translateY(100%)',
                  opacity: visibleSections[index] ? 1 : 0,
                  transition: 'all 0.8s ease-in-out',
                }}
              >
                <Typography
                  variant='body1'
                  fontWeight='400'
                  lineHeight='1.5'
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                >
                  {img.description}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
        <Box sx={{ padding: { xs: 3, sm: 6 }, backgroundColor: '#f9f9f9' }}>
          <Typography
            variant='h2'
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem' },
              fontWeight: 600,
              textAlign: 'center',
              mb: 2,
            }}
          >
            Tertarik dengan BYD BYD Atto 1? 🚐
          </Typography>
          <Typography variant='body1' sx={{ textAlign: 'center', mb: 3 }}>
            Temukan <strong>BYD BYD Atto 1</strong> terbaik hanya di{' '}
            <strong>Dealer Resmi BYD BSD Serpong</strong>. Kami menyediakan
            informasi lengkap seputar <strong>Harga Denza D9</strong>,{' '}
            <strong>Harga Sealion 7</strong>, dan{' '}
            <strong>Harga BYD Atto 1</strong> untuk Anda yang sedang
            mempertimbangkan mobil listrik keluarga, SUV, atau MPV terbaik di
            kelasnya.
          </Typography>
          <Link to='/pricing' style={{ textDecoration: 'none' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 2,
                  padding: 2,
                  minWidth: 250,
                  textAlign: 'center',
                  backgroundColor: '#fff',
                }}
              >
                <Typography variant='h6' fontWeight={600}>
                  Harga BYD M6
                </Typography>
                <Typography variant='body2'>
                  Hubungi kami untuk info harga dan promo cicilan BYD M6
                  terbaru.
                </Typography>
              </Box>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 2,
                  padding: 2,
                  minWidth: 250,
                  textAlign: 'center',
                  backgroundColor: '#fff',
                }}
              >
                <Typography variant='h6' fontWeight={600}>
                  Harga Denza D9
                </Typography>
                <Typography variant='body2'>
                  MPV premium untuk Anda yang mencari kenyamanan dan kemewahan.
                </Typography>
              </Box>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 2,
                  padding: 2,
                  minWidth: 250,
                  textAlign: 'center',
                  backgroundColor: '#fff',
                }}
              >
                <Typography variant='h6' fontWeight={600}>
                  Harga Sealion 7
                </Typography>
                <Typography variant='body2'>
                  SUV listrik dengan fitur canggih dan performa luar biasa.
                </Typography>
              </Box>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 2,
                  padding: 2,
                  minWidth: 250,
                  textAlign: 'center',
                  backgroundColor: '#fff',
                }}
              >
                <Typography variant='h6' fontWeight={600}>
                  Harga BYD Atto 1
                </Typography>
                <Typography variant='body2'>
                  Mobil listrik kompak yang efisien dan ramah lingkungan.
                </Typography>
              </Box>
            </Box>
          </Link>
          <Box sx={{ textAlign: 'center', marginTop: 4 }}>
            <Button
              href='https://api.whatsapp.com/send?phone=6281286864825&text=Halo,%20saya%20tertarik%20dengan%20BYD%20Seagull%20dan%20ingin%20tahu%20harganya'
              target='_blank'
              variant='contained'
              color='primary'
              sx={{ fontWeight: 600 }}
            >
              Konsultasi Harga BYD BYD Atto 1 Sekarang
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default JelajahiSeagull;
